import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POSITIONS = ["Målvakt", "Høyre back", "Venstre back", "Senter", "Høyre ving", "Venstre ving"];

export default function PlayerEditDialog({ open, onOpenChange, player, onSaved }) {
  const [form, setForm] = useState({ full_name: "", date_of_birth: "", jersey_number: "", position: "", email: "", nihf_license: "", nihf_club: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (player) {
      setForm({
        full_name: player.full_name || "",
        date_of_birth: player.date_of_birth || "",
        jersey_number: player.jersey_number ?? "",
        position: player.position || "",
        email: player.email || "",
        nihf_license: player.nihf_license || "",
        nihf_club: player.nihf_club || "",
      });
    }
  }, [player, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name,
        date_of_birth: form.date_of_birth || null,
        jersey_number: form.jersey_number === "" ? undefined : Number(form.jersey_number),
        position: form.position,
        email: form.email,
        nihf_license: form.nihf_license,
        nihf_club: form.nihf_club,
      };
      Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
      await api.patch(`/players/${player.id}`, payload);
      toast.success("Spiller oppdatert");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSubmitting(false);
    }
  };

  if (!player) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl">Rediger spiller</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Navn</Label>
            <Input data-testid="edit-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-sm" />
            <p className="text-xs text-neutral-500 mt-1">Endring av navn trigger ny hockey.no-sjekk.</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Fødselsdato</Label>
            <Input data-testid="edit-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Nummer</Label>
            <Input data-testid="edit-jersey" type="number" min="0" max="99" value={form.jersey_number} onChange={(e) => set("jersey_number", e.target.value)} className="rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Posisjon</Label>
            <Select value={form.position} onValueChange={(v) => set("position", v)}>
              <SelectTrigger data-testid="edit-position" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">E-post</Label>
            <Input data-testid="edit-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">NIHF Lisensnummer</Label>
            <Input data-testid="edit-license" value={form.nihf_license} onChange={(e) => set("nihf_license", e.target.value)} className="rounded-sm" />
            <p className="text-xs text-neutral-500 mt-1">Minst 5 tegn.</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Klubb hos NIHF</Label>
            <Input data-testid="edit-nihf-club" value={form.nihf_club} onChange={(e) => set("nihf_club", e.target.value)} placeholder="F.eks. Strindheim Ishockeyklubb" className="rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Avbryt</Button>
          <Button data-testid="save-edit-btn" disabled={submitting} onClick={save} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">
            {submitting ? "Lagrer…" : "Lagre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
